import React, { memo, useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { useAuth } from 'components/auth';
import BrandLandingDemo from './BrandLandingDemo';
import Reveal from './Reveal';
import TryBrandForm from './TryBrandForm';
import {
  WorkflowMockAct,
  WorkflowMockAudit,
  WorkflowMockBuild,
  WorkflowMockGenerate,
} from './WorkflowMocks';
import {
  BRAND_LANDING_COPY,
  BRAND_LANDING_META,
  BRAND_LANDING_NAV,
} from './copy';
import './BrandLandingPage.css';

const WORKFLOW_MOCKS = {
  build: WorkflowMockBuild,
  generate: WorkflowMockGenerate,
  audit: WorkflowMockAudit,
  act: WorkflowMockAct,
};

const QP_LOGO =
  'https://cdn.questionpro.com/userimages/site_media/questionpro-logo-color.png';

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return el;
}

function BrandLandingPage() {
  const { isAuth } = useAuth();
  const c = BRAND_LANDING_COPY;
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = BRAND_LANDING_META.title;
    const description = upsertMeta('name', 'description', BRAND_LANDING_META.description);
    const ogTitle = upsertMeta('property', 'og:title', BRAND_LANDING_META.title);
    const ogDescription = upsertMeta(
      'property',
      'og:description',
      BRAND_LANDING_META.description,
    );
    const robots = upsertMeta('name', 'robots', 'index,follow');

    let fontLink = document.getElementById(
      'brand-landing-font',
    ) as HTMLLinkElement | null;
    if (!fontLink) {
      fontLink = document.createElement('link');
      fontLink.id = 'brand-landing-font';
      fontLink.rel = 'stylesheet';
      fontLink.href =
        'https://fonts.googleapis.com/css2?family=Fira+Sans:wght@400;500;600;700;800&display=swap';
      document.head.appendChild(fontLink);
    }

    return () => {
      document.title = prevTitle;
      description.remove();
      ogTitle.remove();
      ogDescription.remove();
      robots.remove();
    };
  }, []);

  if (isAuth) {
    return <Redirect to="/ai-audit" />;
  }

  function scrollToId(id: string) {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <div className="brand-landing" id="top" data-testid="brand-landing-page">
      <header className="brand-landing__header">
        <div className="brand-landing__header-inner">
          <a href="#top" className="brand-landing__brand" aria-label="QuestionPro Brand home">
            <img src={QP_LOGO} alt="QuestionPro" />
            <span className="brand-landing__brand-divider" aria-hidden="true" />
            <span>Brand</span>
          </a>
          <nav className="brand-landing__nav" aria-label="Primary">
            {BRAND_LANDING_NAV.map((item) => (
              <a key={item.href} href={item.href}>
                {item.label}
              </a>
            ))}
            <a className="brand-landing__btn brand-landing__btn--nav" href="#signup">
              {c.ctaPrimary}
            </a>
          </nav>
        </div>
      </header>

      <main>
        <section className="brand-landing__hero">
          <Reveal className="brand-landing__wrap" immediate>
            <h1>{c.heroTitle}</h1>
            <p className="brand-landing__hero-body">{c.heroBody}</p>
            <div className="brand-landing__cta-row">
              <a className="brand-landing__btn brand-landing__btn--hero" href="#signup">
                {c.ctaPrimary}
              </a>
              <button
                type="button"
                className="brand-landing__link-btn"
                onClick={() => scrollToId('workflow')}
              >
                {c.ctaSecondary} ↓
              </button>
            </div>
            <p className="brand-landing__hint">{c.alreadyHint}</p>
          </Reveal>
        </section>

        <section className="brand-landing__section" id="demo">
          <div className="brand-landing__wrap">
            <Reveal className="brand-landing__section-head">
              <p className="brand-landing__eyebrow">{c.demoEyebrow}</p>
              <h2>{c.demoTitle}</h2>
              <p className="brand-landing__section-body">{c.demoBody}</p>
            </Reveal>
            <Reveal delay={120}>
              <BrandLandingDemo />
            </Reveal>
          </div>
        </section>

        <section className="brand-landing__section brand-landing__section--soft" id="workflow">
          <div className="brand-landing__wrap">
            <Reveal className="brand-landing__section-head">
              <p className="brand-landing__eyebrow">{c.workflowEyebrow}</p>
              <h2>{c.workflowTitle}</h2>
              <p className="brand-landing__section-body">{c.workflowBody}</p>
            </Reveal>
            <div className="brand-landing__workflow-rows">
              {c.steps.map((step, index) => {
                const Mock = WORKFLOW_MOCKS[step.mock];
                const reversed = index % 2 === 1;
                return (
                  <Reveal
                    key={step.n}
                    as="article"
                    className={`brand-landing__workflow-row${
                      reversed ? ' brand-landing__workflow-row--reverse' : ''
                    }`}
                    delay={index * 60}
                  >
                    <div className="brand-landing__workflow-copy">
                      <div className="brand-landing__workflow-kicker">
                        <span className="brand-landing__step-n">{step.n}</span>
                        <p className="brand-landing__step-label">{step.label}</p>
                      </div>
                      <h3>{step.title}</h3>
                      <p>{step.body}</p>
                      {step.tip ? (
                        <p className="brand-landing__workflow-tip">{step.tip}</p>
                      ) : null}
                    </div>
                    <div className="brand-landing__workflow-visual">
                      <Mock />
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        <section className="brand-landing__section">
          <div className="brand-landing__wrap">
            <Reveal className="brand-landing__section-head">
              <h2>{c.audiencesTitle}</h2>
            </Reveal>
            <div className="brand-landing__audiences">
              {c.audiences.map((a, index) => (
                <Reveal
                  key={a.title}
                  as="article"
                  className="brand-landing__audience"
                  delay={index * 80}
                >
                  <h3>{a.title}</h3>
                  <p>{a.body}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="brand-landing__section brand-landing__section--soft" id="faq">
          <div className="brand-landing__wrap">
            <Reveal className="brand-landing__section-head">
              <p className="brand-landing__eyebrow">{c.faqEyebrow}</p>
              <h2>{c.faqTitle}</h2>
              <p className="brand-landing__section-body">{c.faqBody}</p>
            </Reveal>
            <div className="brand-landing__faq-list">
              {c.faqs.map((item, index) => (
                <Reveal
                  key={item.q}
                  as="details"
                  delay={index * 60}
                  open={openFaq === index}
                >
                  <summary
                    onClick={(e) => {
                      e.preventDefault();
                      setOpenFaq((current) =>
                        current === index ? null : index,
                      );
                    }}
                  >
                    {item.q}
                  </summary>
                  <p>{item.a}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="brand-landing__section brand-landing__section--dark brand-landing__signup">
          <Reveal className="brand-landing__wrap">
            <h2>{c.signupTitle}</h2>
            <p className="brand-landing__section-body">{c.signupBody}</p>
            <TryBrandForm id="signup" />
          </Reveal>
        </section>
      </main>

      <footer className="brand-landing__footer">
        <div className="brand-landing__wrap">{c.footerNote}</div>
      </footer>
    </div>
  );
}

export default memo(BrandLandingPage);
